import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircleRounded,
  CancelRounded,
  HourglassEmptyRounded,
  ErrorOutlineRounded,
} from '@mui/icons-material';
import type { DocumentRequirement, ReviewableDocument } from '../hooks/use-admission-detail';

interface Props {
  requirements: DocumentRequirement[];
  documents: ReviewableDocument[];
  busy?: boolean;
  onVerify: (type: string) => void;
  onReject: (type: string, reason: string) => void;
}

type Status = 'missing' | 'pending' | 'verified' | 'expired' | 'rejected';

function statusOf(type: string, docs: ReviewableDocument[], now = new Date()): Status {
  const matching = docs.filter((d) => d.type === type);
  if (matching.length === 0) return 'missing';
  // VENCIDO ES DISTINTO DE FALTA, y la ficha tiene que decirlo: ante un «falta»
  // alguien sale a buscar un documento que el postulante YA entregó. Un
  // verificado VIGENTE gana sobre uno vencido del mismo tipo — es exactamente
  // lo que pasa cuando suben el reemplazo.
  const verified = matching.filter((d) => d.verified);
  if (verified.some((d) => !d.expirationDate || d.expirationDate >= now)) return 'verified';
  if (verified.length > 0) return 'expired';
  if (matching.some((d) => d.rejectionReason)) return 'rejected';
  return 'pending';
}

export function AdmissionDocumentsReview({
  requirements,
  documents,
  busy,
  onVerify,
  onReject,
}: Props) {
  const { t } = useTranslation('sis');
  const [rejectType, setRejectType] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const STATUS_META: Record<
    Status,
    { color: 'default' | 'success' | 'error' | 'warning'; icon: JSX.Element; key: string }
  > = {
    missing: { color: 'default', icon: <ErrorOutlineRounded fontSize="small" />, key: 'missing' },
    pending: { color: 'warning', icon: <HourglassEmptyRounded fontSize="small" />, key: 'pending' },
    verified: { color: 'success', icon: <CheckCircleRounded fontSize="small" />, key: 'verified' },
    expired: {
      color: 'warning',
      icon: <HourglassEmptyRounded fontSize="small" />,
      key: 'expired',
    },
    rejected: { color: 'error', icon: <CancelRounded fontSize="small" />, key: 'rejected' },
  };

  // Requirements + any uploaded documents that don't match a requirement.
  const reqTypes = new Set(requirements.map((r) => r.type));
  const extraTypes = [...new Set(documents.map((d) => d.type))].filter((ty) => !reqTypes.has(ty));

  const rows: { type: string; required: boolean; description?: string }[] = [
    ...requirements.map((r) => ({
      type: r.type,
      required: r.required,
      description: r.description,
    })),
    ...extraTypes.map((ty) => ({ type: ty, required: false })),
  ];

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" fontWeight={700}>
        {t('admissionDocs.title')}
      </Typography>
      {rows.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('admissionDocs.none')}
        </Typography>
      )}
      {rows.map((row) => {
        const status = statusOf(row.type, documents);
        const meta = STATUS_META[status];
        const rejected = documents.find((d) => d.type === row.type && d.rejectionReason);
        return (
          <Box
            key={row.type}
            sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600}>
                  {row.type}
                  {row.required && (
                    <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                      *
                    </Box>
                  )}
                </Typography>
                {row.description && (
                  <Typography variant="caption" color="text.secondary">
                    {row.description}
                  </Typography>
                )}
              </Box>
              <Chip
                size="small"
                color={meta.color}
                icon={meta.icon}
                label={t(`admissionDocs.status.${meta.key}`)}
              />
              {status !== 'missing' && status !== 'verified' && status !== 'expired' && (
                <Button size="small" disabled={!!busy} onClick={() => onVerify(row.type)}>
                  {t('admissionDocs.verify')}
                </Button>
              )}
              {status !== 'missing' && status !== 'rejected' && (
                <Button
                  size="small"
                  color="error"
                  disabled={!!busy}
                  onClick={() => {
                    setRejectType(row.type);
                    setReason('');
                  }}
                >
                  {t('admissionDocs.reject')}
                </Button>
              )}
            </Stack>
            {rejected?.rejectionReason && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {rejected.rejectionReason}
              </Alert>
            )}
          </Box>
        );
      })}

      <Dialog open={!!rejectType} onClose={() => setRejectType(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('admissionDocs.rejectTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label={t('admissionDocs.reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectType(null)}>{t('admissionDocs.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!reason.trim()}
            onClick={() => {
              if (rejectType) onReject(rejectType, reason.trim());
              setRejectType(null);
            }}
          >
            {t('admissionDocs.reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
